import { useSyncExternalStore } from 'react';
import type { BossId, PerkId } from '@cradle7/shared';
import { computeRunScore } from '@cradle7/shared';
import { addRun, getRuns, setRuns, getProgressSnapshot, subscribeProgress, type ClientRunRecord } from './progressStore';

export type RunOutcome = 'won' | 'lost';

/**
 * Public record shape kept stable for existing UI callers (HUD, EndScreen,
 * Stats section, achievements). Score is optional because pre-Phase-1b
 * records in localStorage may not have it.
 */
export type RunRecord = {
  id: string;
  ts: number;
  outcome: RunOutcome;
  kills: number;
  loot: number;
  perks: PerkId[];
  durationMs: number;
  seed: number;
  score?: number;
  /** Phase 2h: which boss (if any) was defeated on this run. Present only when outcome === 'won'. */
  bossDefeated?: BossId;
};

/** Append a run. Score is computed locally if missing (server will recompute on submit). */
export function saveRunRecord(rec: RunRecord): void {
  const score = rec.score ?? computeRunScore(rec);
  const client: ClientRunRecord = {
    id: rec.id,
    ts: rec.ts,
    outcome: rec.outcome,
    kills: rec.kills,
    loot: rec.loot,
    perks: rec.perks,
    durationMs: rec.durationMs,
    seed: rec.seed,
    score,
    ...(rec.bossDefeated ? { bossDefeated: rec.bossDefeated } : {}),
  };
  addRun(client);
}

/** Full history, newest first. */
export function getRunHistory(): RunRecord[] {
  return getRuns().map(toRunRecord);
}

/** Most-recent N records. */
export function getRecentRuns(n = 3): RunRecord[] {
  return getRuns().slice(0, n).map(toRunRecord);
}

export type AggregateStats = {
  totalRuns: number;
  wins: number;
  losses: number;
  winRate: number;
  bestKills: number;
};

/** Aggregate stats: server snapshot when available, otherwise computed locally. */
export function getAggregateStats(): AggregateStats {
  const snap = getProgressSnapshot();
  if (snap) {
    return {
      totalRuns: snap.totalRuns,
      wins: snap.wins,
      losses: snap.totalRuns - snap.wins,
      winRate: snap.totalRuns > 0 ? snap.wins / snap.totalRuns : 0,
      bestKills: snap.bestKills,
    };
  }
  const all = getRuns();
  const totalRuns = all.length;
  const wins = all.filter((r) => r.outcome === 'won').length;
  const losses = totalRuns - wins;
  const bestKills = all.reduce((best, r) => Math.max(best, r.kills), 0);
  const winRate = totalRuns > 0 ? wins / totalRuns : 0;
  return { totalRuns, wins, losses, winRate, bestKills };
}

/** Wipe history (used for a future "reset progress" action). */
export function clearRunHistory(): void {
  setRuns([]);
}

/** Subscribe to runs + progress changes; returns unsubscribe. */
export { subscribeProgress as subscribeRunHistory };

/** Small UUID-ish id (kept for backward compatibility with callers). */
export function generateRunId(): string {
  return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function toRunRecord(c: ClientRunRecord): RunRecord {
  return { ...c };
}

// ---------------------------------------------------------------------------
// React hook — re-renders caller whenever runHistory / server snapshot changes
// ---------------------------------------------------------------------------

let _lastSnapshot: { history: RunRecord[]; recent: RunRecord[]; stats: AggregateStats } | null = null;
let _lastRunsRef: ClientRunRecord[] | null = null;

function _getSnapshot(): { history: RunRecord[]; recent: RunRecord[]; stats: AggregateStats } {
  const runs = getRuns();
  if (_lastSnapshot && _lastRunsRef === runs) return _lastSnapshot;
  const history = runs.map(toRunRecord);
  const next = {
    history,
    recent: history.slice(0, 3),
    stats: getAggregateStats(),
  };
  _lastSnapshot = next;
  _lastRunsRef = runs;
  return next;
}

/**
 * React hook: re-renders the caller whenever runHistory or the server progress
 * snapshot changes.  Returns history, recent (first 3), and aggregate stats.
 */
export function useRunHistorySnapshot(): { history: RunRecord[]; recent: RunRecord[]; stats: AggregateStats } {
  return useSyncExternalStore(subscribeProgress, _getSnapshot, _getSnapshot);
}
